import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import NumberInput from 'components/Inputs/NumberInput'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import changeCustomerServicesValidator from 'validators/changeCustomerServicesValidator'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  FormSection,
  FormField,
  Row,
  TextRow,
  CheckRow,
  Label,
  SubTitle,
  InfoText,
  WarningText,
  MoneyTextMask,
  Input
} from './styles'

export default function ChangeCustomerServicesModal ({
  visible,
  customer,
  service: customerService,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [services, setServices] = useState ([])
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const serviceOptions = services.map (service => ({
    label: service.nickname || service.name,
    value: service
  }))
  
  const exchangeOptions = [
    { label: 'Trocar por crédito', value: 'credit_exchange' },
    { label: 'Trocar por outros serviços', value: 'services_exchange' }
  ]

  useEffect (() => {
    getData ()
  }, [visible])

  const getData = async () => {
    try {
      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active&cutilei_services=false`,
        requestConfig
      )
      setServices (services.filter (service => service.id !== customerService.id))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleConfirmDialog = () => setShowConfirmDialog (!showConfirmDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Trocar serviços</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={changeCustomerServicesValidator}
          initialValues={{
            'business_id': businessId,
            'original_service': customerService,
            'amount': customerService.available_amount,
            'exchange_option': exchangeOptions[0],
            'new_services': [{ id: '', name: '', amount: 1, price: 0 }],
            'ignore_credits': false
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const serviceBody = {
                ...values,
                exchange_option: values.exchange_option.value
              }

              const { data: creditValue } = await cutileiApi.put (
                `/customers/${customer.id}/services/${customerService.id}`,
                serviceBody,
                requestConfig
              )

              setSubmitting (false)
              handleConfirm (creditValue)
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {function ChangeCustomerServicesForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleSubmit
          }) {
            const servicePrice = useMemo (() => {
              const bundleService = values.original_service.bundle?.services.find (service => (
                service.id === values.original_service.id
              ))

              return bundleService?.discount_price || 0
            }, [values.original_service])

            const newServicesTotalPrice = useMemo (() => {
              return values.new_services.reduce ((total, service) => (
                total + (service.amount * service.price)
              ), 0)
            }, [values.new_services])

            const creditValue = useMemo (() => {
              return (values.amount * servicePrice) - newServicesTotalPrice
            }, [values.amount, servicePrice, newServicesTotalPrice])

            const handleAddService = () => {
              setFieldValue ('new_services', [
                ...values.new_services,
                { id: '', name: '', amount: 1, price: 0 }
              ])
            }

            const handleDeleteService = index => {
              setFieldValue (
                'new_services',
                values.new_services.map ((s, i) => ({...s, idx: i})).filter (s => s.idx !== index)
              )
            }

            if (loading) return <DialogBody><Loading/></DialogBody>

            return (
              <>
                <DialogBody>
                  <Form onSubmit={handleSubmit} onKeyDown={submitOnEnter(toggleConfirmDialog, isSubmitting)}>
                    <Row>
                      <FormField stretch={5}>
                        <Label>Serviço adquirido</Label>
                        <Input value={values.original_service.name} disabled={true}/>
                      </FormField>
                      <FormField stretch={2}>
                        <Label htmlFor='amount'>
                          Quantidade
                        </Label>
                        <NumberInput
                          name='amount'
                          value={values.amount}
                          minValue={1}
                          maxValue={values.original_service.available_amount}
                          onChange={setFieldValue}
                          style={{width: '100%'}}
                        />
                      </FormField>
                    </Row>
                    <TextRow>
                      <SubTitle>Valor total dos serviços:</SubTitle>
                      <MoneyTextMask value={values.amount * servicePrice}/>
                    </TextRow>
                    <Row>
                      <FormField>
                        <Label htmlFor='exchange_option'>
                          Tipo de troca
                        </Label>
                        <Select
                          name='exchange_option'
                          placeholder='Selecionar...'
                          value={values.exchange_option}
                          options={exchangeOptions}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                        />
                      </FormField>
                    </Row>
                    {values.exchange_option?.value === 'credit_exchange' && (
                      <FormSection>
                        <TextRow>
                          <InfoText>O cliente receberá um crédito no valor de:</InfoText>
                          <MoneyTextMask value={values.amount * servicePrice} style={{fontSize: 14}}/>
                        </TextRow>
                        <InfoText>
                          Este valor poderá ser usado para pagar serviços ou produtos em seu salão.
                        </InfoText>
                      </FormSection>
                    )}
                    {values.exchange_option?.value === 'services_exchange' && (
                      <FormSection>
                        <SubTitle>Serviços para troca</SubTitle>
                        {values.new_services.map ((_, index) => (
                          <Row key={index} style={{alignItems: 'flex-start'}}>
                            <FormField stretch={5}>
                              {index === 0 && <Label>Serviço</Label>}
                              <Select
                                name={`new_services[${index}].name`}
                                placeholder='Serviço...'
                                value={values.new_services[index].name ? {
                                  label: values.new_services[index].name,
                                  value: values.new_services[index]
                                } : null}
                                options={serviceOptions}
                                onChange={(_, option) => {
                                  setFieldValue (`new_services[${index}]`, {
                                    ...values.new_services[index],
                                    id: option.value.id,
                                    name: option.label,
                                    price: option.value.price
                                  })
                                }}
                                onBlur={setFieldTouched}
                                error={errors.new_services?.[index]?.name}
                                touched={touched.new_services?.[index]?.name}
                              />
                              {touched.new_services?.[index]?.name && errors.new_services?.[index]?.name && (
                                <WarningText>
                                  {errors.new_services[index].name}
                                </WarningText>
                              )}
                            </FormField>
                            <FormField stretch={2}>
                              {index === 0 && <Label>Quantidade</Label>}
                              <NumberInput
                                name={`new_services[${index}].amount`}
                                value={values.new_services[index].amount}
                                minValue={1}
                                onChange={setFieldValue}
                                style={{width: '100%'}}
                              />
                            </FormField>
                            {values.new_services.length > 1 && (
                              <SecondaryButton
                                type='button'
                                onClick={() => handleDeleteService (index)}
                                leftIcon={<FaTrash color='#FFFFFF' size={12} style={{marginBottom: 1}}/>}
                                style={{marginTop: index === 0 ? 25 : 0}}
                                color='#FF3939'
                                width={50}
                              />
                            )}
                          </Row>
                        ))}
                        <SecondaryButton
                          label='Serviço'
                          type='button'
                          onClick={handleAddService}
                          leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                          style={{marginBottom: 10}}
                          color='#606060'
                          width={120}
                        />
                        <TextRow>
                          <SubTitle style={{marginBottom: 0}}>
                            Valor total dos serviços:
                          </SubTitle>
                          <MoneyTextMask value={newServicesTotalPrice}/>
                        </TextRow>
                        <TextRow>
                          <SubTitle>Crédito/dívida restante:</SubTitle>
                          <MoneyTextMask value={creditValue}/>
                        </TextRow>
                        <CheckRow>
                          <CheckBox
                            id='ignore_credits'
                            value={values.ignore_credits}
                            onValueChange={value => setFieldValue ('ignore_credits', value)}
                          />
                          <Label htmlFor='ignore_credits'>
                            Realizar a troca sem contabilizar os créditos/dívidas
                          </Label>
                        </CheckRow>
                      </FormSection>
                    )}
                    <AlertDialog
                      visible={showConfirmDialog}
                      title='Atenção!'
                      message='Deseja realmente realizar a troca de serviços deste cliente?'
                      confirmText='Sim'
                      cancelText='Não'
                      onConfirm={handleSubmit}
                      onClose={toggleConfirmDialog}
                    />
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Confirmar troca'
                    onClick={toggleConfirmDialog}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={160}
                  />
                </DialogFooter>
              </>
            ) 
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
